import _ from "lodash";
import ReactEcharts from "echarts-for-react";
import { DateTime, Duration } from "luxon";
import { hexColor, rgbColor } from "../../../enums/colors";
import { EquipmentAttributesById, Event, EventsCalculation, FShift, UsageDataset } from "../../../types/types";
import { t } from "i18next";
import { TbCircleFilled } from "react-icons/tb";
import { useMemo } from "react";
import { useAppStore } from "../../../stores/RootStore";
import { calculateEvents, calculateUsage } from "../../../lib/calculations";

// 0: "equipment_item_id"
// 1: "event_date"
// 2: "shift_date"
// 3: "start"
// 4: "start_ts"
// 5: "finish"
// 6: "finish_ts"
// 7: "duration"
// 8: "state"
// 9: "equipment_item_status"
// 10: "personnel_id"
// 11: "equipment_item_division_id"
// 12: "personnel_division_id"
// 13: "state_changed"
// 14: "status_changed"
// 15: "person_changed"

const PieLoadChartByEvents = ({events, shift, equipmnets_count = 1, days_count = 1}: {events: Event[], shift: FShift, equipmnets_count?: number, days_count?: number}) => {
  const {theme} = useAppStore()
  let duration = (shift.start_at < shift.end_at ? shift.end_at.diff(shift.start_at).toMillis() / 1000 : DateTime.fromSeconds(shift.end_at.toSeconds() + 86400).diff(shift.start_at).toMillis() / 1000) * equipmnets_count * days_count
  const calculations = useMemo(() => {return calculateEvents(events, duration)}, [events]) 
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.broken, hexColor.repair, hexColor.maintenance, hexColor.turned_off]

  const getOptions = () => ({
    // tooltip: {
    //   trigger: "item",
    //   formatter: "{a} <br/>{b}: {c}с ({d}%)"
    // },
    series: [
      {
        name: "Название оборудования",
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: calculations?.totalWorked!, color: hexColor.worked },
          { name: "idle", value: calculations?.totalStopped!, color: hexColor.stopped },
          { name: "broken", value: calculations?.totalBroken!, color: hexColor.broken },
          { name: "repair", value: calculations?.totalRepair!, color: hexColor.repair },
          { name: "maintenance", value: calculations?.totalMaintenance!, color: hexColor.maintenance },
          { name: "turned_off", value: calculations?.totalOther! + calculations?.totalTurnedOff, color: hexColor.turned_off},
        ],
        
      },
    ]
  });

  return (
      <div className="flex flex-col w-full text-sm my-4 px-2">
        <div className="grid my-4 gap-4">
          <div className="flex w-full justify-center px-6" style={{ gridColumn: 1,  gridRow: 1}}>
            <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 2,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
              <span className="font-semibold">{t('equipment_worked')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_worked_time')}:</span>
              <span className="font-medium text-base">{calculations.totalWorkedTime} ({calculations.totalWorkedPercent.toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 3,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
              <span className="font-semibold">{t('equipment_idle')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_idle_time')}:</span>
              <span className="font-medium text-base">{calculations.totalStoppedTime} ({calculations.totalStoppedPercent.toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_maintenance_time')}:</span>
              <span className="font-medium text-base">{calculations.totalMaintenanceTime} ({calculations.totalMaintenancePercent.toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 4,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.repair}}/>
              <span className="font-semibold">{t('equipment_service')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_broken_time')}:</span>
              <span className="font-medium text-base">{calculations.totalBrokenTime} ({calculations.totalBrokenPercent.toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_repair_time')}:</span>
              <span className="font-medium text-base">{calculations.totalRepairTime} ({calculations.totalRepairPercent.toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 5,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
              <span className="font-semibold">{t('equipment_turnedOff')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_turnedOff_time')}:</span>
              <span className="font-medium text-base">{calculations.totalTurnedOffTime} ({calculations.totalTurnedOffPercent.toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_other_time')}:</span>
              <span className="font-medium text-base">{calculations.totalOtherTime} ({calculations.totalOtherPercent.toFixed(2)} %)</span>
            </div>
          </div>
        </div>
      </div>
  );
}

const PieLoadChartByUsage = ({usage, shift}: {usage: UsageDataset[], shift: FShift}) => {
  const {theme} = useAppStore()
  let days = _.uniq(usage.map(i => i.at)).length
  let duration = (shift.start_at < shift.end_at ? shift.end_at.diff(shift.start_at).toMillis() / 1000 : DateTime.fromSeconds(shift.end_at.toSeconds() + 86400).diff(shift.start_at).toMillis() / 1000) * days
  const calculations = useMemo(() => {return calculateUsage(usage, duration)}, [usage]) 
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.broken, hexColor.repair, hexColor.maintenance, hexColor.turned_off]

  const getOptions = () => ({
    // tooltip: {
    //   trigger: "item",
    //   formatter: "{a} <br/>{b}: {c}с ({d}%)"
    // },
    series: [
      {
        name: "Название оборудования",
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: calculations?.totalWorked!, color: hexColor.worked },
          { name: "idle", value: calculations?.totalStopped!, color: hexColor.stopped },
          { name: "broken", value: calculations?.totalBroken!, color: hexColor.broken },
          { name: "repair", value: calculations?.totalRepair!, color: hexColor.repair },
          { name: "maintenance", value: calculations?.totalMaintenance!, color: hexColor.maintenance },
          { name: "turned_off", value: calculations?.totalOther! + calculations?.totalTurnedOff, color: hexColor.turned_off},
        ],
        
      },
    ]
  });

  return (
      <div className="flex flex-col w-full text-sm my-4 px-2">
        <div className="grid my-4 gap-4">
          <div className="flex w-full justify-center px-6" style={{ gridColumn: 1,  gridRow: 1}}>
            <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 2,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
              <span className="font-semibold">{t('equipment_worked')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_worked_time')}:</span>
              <span className="font-medium text-base">{calculations.totalWorkedTime} ({calculations.totalWorkedPercent.toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 3,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
              <span className="font-semibold">{t('equipment_idle')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_idle_time')}:</span>
              <span className="font-medium text-base">{calculations.totalStoppedTime} ({calculations.totalStoppedPercent.toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_maintenance_time')}:</span>
              <span className="font-medium text-base">{calculations.totalMaintenanceTime} ({calculations.totalMaintenancePercent.toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 4,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.repair}}/>
              <span className="font-semibold">{t('equipment_service')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_broken_time')}:</span>
              <span className="font-medium text-base">{calculations.totalBrokenTime} ({calculations.totalBrokenPercent.toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_repair_time')}:</span>
              <span className="font-medium text-base">{calculations.totalRepairTime} ({calculations.totalRepairPercent.toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 5,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
              <span className="font-semibold">{t('equipment_turnedOff')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_turnedOff_time')}:</span>
              <span className="font-medium text-base">{calculations.totalTurnedOffTime} ({calculations.totalTurnedOffPercent.toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_other_time')}:</span>
              <span className="font-medium text-base">{calculations.totalOtherTime} ({calculations.totalOtherPercent.toFixed(2)} %)</span>
            </div>
          </div>
        </div>
      </div>
  );
}

const PieLoadChartForPerson = ({data}: {data: {
  totalWorked: number; 
  totalStopped: number; 
  totalInteractionTime: number; 
  totalShiftsTime: number; 
  // totalDetectedShiftsTime: number;
  }}) => {
  const {theme} = useAppStore()
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.turned_off]

  const getOptions = () => ({
    // tooltip: {
    //   trigger: "item",
    //   formatter: "{a} <br/>{b}: {c}с ({d}%)"
    // },
    series: [
      {
        name: "Название оборудования",
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: data.totalWorked, color: hexColor.worked },
          { name: "idle", value: data.totalStopped, color: hexColor.stopped },
        ],
        
      },
    ]
  });

  return (
      <div className="flex flex-col w-full text-sm my-4 px-2">
        <div className="grid my-4 gap-4">
          <div className="flex w-full justify-center px-6" style={{ gridColumn: 1,  gridRow: 1}}>
            <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
          </div>

          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 2,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
              <span className="font-semibold">{t('worked')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('person_worked_time_by_interaction')}:</span>
              <span className="font-medium text-base">{Duration.fromMillis(data.totalWorked * 1000).toFormat("hh:mm:ss")} ({((data.totalWorked / data.totalInteractionTime) * 100).toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('person_worked_time_by_choosen_shift')}:</span>
              <span className="font-medium text-base">
                {((data.totalWorked / data.totalShiftsTime) * 100).toFixed(2)} % 
                {/* ({((data.totalWorked / data.totalDetectedShiftsTime) * 100).toFixed(2)} %) */}
              </span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 3,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
              <span className="font-semibold">{t('stopped')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('person_stopped_time_by_interaction')}:</span>
              <span className="font-medium text-base">{Duration.fromMillis(data.totalStopped * 1000).toFormat("hh:mm:ss")} ({((data.totalStopped / data.totalInteractionTime) * 100).toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('person_worked_time_by_choosen_shift')}:</span>
              <span className="font-medium text-base">
                {((data.totalStopped / data.totalShiftsTime) * 100).toFixed(2)} % 
                {/* ({((data.totalStopped / data.totalDetectedShiftsTime) * 100).toFixed(2)} %) */}
              </span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 4,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.other}}/>
              <span className="font-semibold">{t('interaction')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('available_time_by_interaction')}:</span>
              <span className="font-medium text-base">{Duration.fromMillis(data.totalInteractionTime * 1000).toFormat("hh:mm:ss")}</span>
              <span className="text-secondary-foreground">{t('available_time_by_choosen_shift')}:</span>
              <span className="font-medium text-base">
                {Duration.fromMillis(data.totalShiftsTime * 1000).toFormat("hh:mm:ss")} 
                {/* ({Duration.fromMillis(data.totalDetectedShiftsTime * 1000).toFormat("hh:mm:ss")}) */}
              </span>
            </div>
          </div>
        </div>
      </div>
  );
}


const MonitorPieLoadChart = ({data}: {data: EventsCalculation}) => {
  const {theme} = useAppStore()
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.broken, hexColor.repair, hexColor.maintenance, hexColor.service,  hexColor.turned_off]

  

  const getOptions = () => ({
    // tooltip: {
    //   trigger: "item",
    //   formatter: "{a} <br/>{b}: {c}с ({d}%)"
    // },
    series: [
      {
        name: "Название оборудования",
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: data?.itemsAvailability.worked.seconds!, color: hexColor.worked },
          { name: "idle", value: data?.itemsAvailability.stopped.seconds!, color: hexColor.stopped },
          { name: "broken", value: data?.itemsBroken.total.seconds!, color: hexColor.broken },
          { name: "repair", value: data?.itemsRepair.total.seconds!, color: hexColor.repair },
          { name: "maintenance", value: data?.itemsMaintenance.total.seconds!, color: hexColor.maintenance },
          { name: "service", value: data?.itemsService.total.seconds!, color: hexColor.service },
          { name: "turned_off", value: data?.itemsOther.total.seconds! + data?.itemsAvailability.off.seconds + data.itemsUnavailability.off.seconds, color: hexColor.turned_off},
        ],
        
      },
    ]
  });

  return (
      <div className="flex flex-col w-full text-sm my-4 px-2">
        <div className="grid grid-cols-1 lg:grid-cols-3 my-4 gap-4">
          <div className="flex w-full justify-center lg:px-6">
            <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
          </div>
          <div className="flex flex-col w-full gap-3 lg:border-l border-border lg:px-6">
            <div className="grid grid-cols-2">
              <div className="flex font-semibold">{t('availability')}</div>
              <div className="flex font-semibold">{data.itemsAvailability.total.time} ({((data.itemsAvailability.total.seconds / (data.itemsAvailability.total.seconds + data.itemsUnavailability.total.seconds)) * 100).toFixed(2)} %)</div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
                  <span className="text-secondary-foreground">{t('worked')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsAvailability.worked.time} ({(data.itemsAvailability.worked.percent * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('turned_off')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsAvailability.off.time} ({(data.itemsAvailability.off.percent * 100).toFixed(2)} %)</span>
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
                  <span className="text-secondary-foreground">{t('stopped')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsAvailability.stopped.time} ({(data.itemsAvailability.stopped.percent * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.maintenance}}/>
                  <span className="text-secondary-foreground">{t('maintenance')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsMaintenance.total.time} ({(data.itemsMaintenance.total.percent * 100).toFixed(2)} %)</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 lg:border-l border-border lg:px-6">
            <div className="grid grid-cols-2">
              <div className="flex font-semibold">{t('unavailability')}</div>
              <div className="flex font-semibold">{data.itemsUnavailability.total.time} ({((data.itemsUnavailability.total.seconds / (data.itemsAvailability.total.seconds + data.itemsUnavailability.total.seconds)) * 100).toFixed(2)} %)</div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.broken}}/>
                  <span className="text-secondary-foreground">{t('broken')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsBroken.total.time} ({(data.itemsBroken.total.percent * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.repair}}/>
                  <span className="text-secondary-foreground">{t('repair')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsRepair.total.time} ({(data.itemsRepair.total.percent * 100).toFixed(2)} %)</span>
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.service}}/>
                  <span className="text-secondary-foreground">{t('service')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsService.total.time} ({(data.itemsService.total.percent * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('turned_off')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsUnavailability.off.time} ({(data.itemsUnavailability.off.percent * 100).toFixed(2)} %)</span>
              </div>
            </div>
          </div>


          {/* <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 2,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/> 
              <span className="font-semibold">{t('equipment_worked')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_worked_time')}:</span>
              <span className="font-medium text-base">{data.itemsAvailability.worked.time} ({(data.itemsAvailability.worked.percent  * 100).toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 3,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
              <span className="font-semibold">{t('equipment_idle')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_idle_time')}:</span>
              <span className="font-medium text-base">{data.itemsAvailability.stopped.time} ({(data.itemsAvailability.stopped.percent * 100).toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_maintenance_time')}:</span>
              <span className="font-medium text-base">{data.itemsMaintenance.total.seconds} ({(data.itemsMaintenance.total.percent * 100).toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 4,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.repair}}/>
              <span className="font-semibold">{t('equipment_service')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_broken_time')}:</span>
              <span className="font-medium text-base">{data.itemsBroken.total.time} ({(data.itemsBroken.total.percent * 100).toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_repair_time')}:</span>
              <span className="font-medium text-base">{data.itemsRepair.total.time} ({(data.itemsRepair.total.percent * 100).toFixed(2)} %)</span>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 5,  gridRow: 1}}>
            <div className="flex items-center space-x-2">
              <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
              <span className="font-semibold">{t('equipment_turnedOff')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-secondary-foreground">{t('equipment_turnedOff_time')}:</span>
              <span className="font-medium text-base">{Duration.fromObject({seconds: data.itemsAvailability.off.seconds + data.itemsUnavailability.off.seconds}).toFormat("hh:mm:ss")} ({((data.itemsAvailability.off.percent + data.itemsUnavailability.off.percent) * 100).toFixed(2)} %)</span>
              <span className="text-secondary-foreground">{t('equipment_other_time')}:</span>
              <span className="font-medium text-base">{data.itemsOther.total.time} ({(data.itemsOther.total.percent  * 100).toFixed(2)} %)</span>
            </div>
          </div> */}
        </div>
      </div>
  );
}

const EquipmentPieLoadChart = ({data}: {data: EventsCalculation}) => {
  const {theme} = useAppStore()
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.turned_off]

  

  const getOptions = () => ({
    // tooltip: {
    //   trigger: "item",
    //   formatter: "{a} <br/>{b}: {c}с ({d}%)"
    // },
    series: [
      {
        name: "Название оборудования",
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: data?.itemsAvailability.worked.seconds!, color: hexColor.worked },
          { name: "idle", value: data?.itemsAvailability.stopped.seconds!, color: hexColor.stopped },
          { name: "turned_off", value: data?.itemsAvailability.off.seconds! + data?.itemsUnavailability.total.seconds, color: hexColor.turned_off},
        ],
      },
    ]
  });

  return (
      <div className="flex flex-col w-full text-sm my-4 px-2">
        <div className="grid my-4 gap-4">
          <div className="flex w-full justify-center px-6" style={{ gridColumn: 1,  gridRow: 1}}>
            <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 2,  gridRow: 1}}>
            <div className="grid grid-cols-2">
              <div className="flex font-semibold">{t('availability')}</div>
              <div className="flex font-semibold">{data.itemsAvailability.total.time}</div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
                  <span className="text-secondary-foreground">{t('worked')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsAvailability.worked.time} ({(data.itemsAvailability.worked.percent * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
                  <span className="text-secondary-foreground">{t('stopped')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsAvailability.stopped.time} ({(data.itemsAvailability.stopped.percent * 100).toFixed(2)} %)</span>
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <span className="text-secondary-foreground">{t('maintenance')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsMaintenance.total.time} ({(data.itemsMaintenance.total.percent * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <span className="text-secondary-foreground">{t('turned_off')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsAvailability.off.time} ({(data.itemsAvailability.off.percent * 100).toFixed(2)} %)</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col w-full gap-3 border-l border-border px-6" style={{ gridColumn: 3,  gridRow: 1}}>
            <div className="grid grid-cols-2">
              <div className="flex font-semibold">{t('unavailability')}</div>
              <div className="flex font-semibold">{data.itemsUnavailability.total.time}</div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('broken')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsBroken.total.time} ({data.itemsBroken.total.percent.toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('repair')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsRepair.total.time} ({data.itemsRepair.total.percent.toFixed(2)} %)</span>
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('service')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsService.total.time} ({data.itemsService.total.percent.toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                  <span className="text-secondary-foreground">{t('turned_off')}:</span>
                </div>
                <span className="font-medium text-base">{data.itemsUnavailability.off.time} ({data.itemsUnavailability.off.percent.toFixed(2)} %)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export {
  PieLoadChartForPerson,
  PieLoadChartByEvents,
  PieLoadChartByUsage,


  MonitorPieLoadChart,
  EquipmentPieLoadChart
}